<template>
  <component
    :is="$sbLinkFix(blok.link) ? NuxtLink : 'div'"
    v-editable="blok"
    class="EventBanner"
    :to="blok.link?.linktype === 'url' ? blok.link.url : $sbLinkFix(blok.link)"
    :target="$sbLinkTarget(blok.link)"
  >
    <div
      class="w-full aspect-wide relative mb-16"
      :class="{
        'bg-light': !blok.image?.filename
      }"
    >
      <nuxt-img
        v-if="blok.image && blok.image.filename"
        preset="standard"
        loading="lazy"
        :sizes="`sm:${size === 'full' ? '100':'50'}vw desk:${sizeDesktop === 'full' ? '648':'428'}px`"
        class="h-full w-full object-cover"
        :src="blok.image.filename"
        :alt="blok.image.alt || blok.headline"
      />

      <div class="absolute top-12 desk:top-24 left-12 desk:left-24 text-center w-48">
        <div class="bg-criticalDark type-headline-xxs text-lightest uppercase">{{ startMonth.replace('.', '') }}</div>
        <div class="bg-lightest type-headline-xl desk:type-headline-2xl py-4">{{ startDay.replace('.', '') }}</div>
      </div>

      <div
        v-if="blok.price1 || blok.price2"
        class="absolute rounded-full
        h-64 w-64 desk:h-96 desk:w-96
        bottom-12 right-12 desk:bottom-16 desk:right-16
        bg-darkest text-lightest
        flex flex-col items-center justify-center"
      >
        <div class="mobOnly:text-[8px] font-medium desk:type-headline-xxs whitespace-nowrap leading-single mb-2">{{ blok.price1 }}</div>
        <div class="type-headline-xs desk:type-lg whitespace-nowrap mobOnly:leading-single uppercase">{{ blok.price2 }}</div>
      </div>
    </div>

    <div class="mobOnly:px-12">
      <div class="mb-8">
        <span class="type-tagline dotted-border">{{ blok.category }}</span>
      </div>
      <div
        class=" desk:type-headline-xl mb-8"
        :class="{
          'mobOnly:type-headline-sm': size === 'half',
          'mobOnly:type-headline-lg': size === 'full',
        }"
      >
        {{ blok.headline }}
      </div>
      <div
        class="type-sm-medium flex"
        :class="{
          'mobOnly:type-xxs-medium mobOnly:flex-col': size === 'half'
        }"
      >
        <div class="mr-16 flex">
          <img
            src="/icons/calendar.svg"
            class="w-16 desk:w-20 mr-8"
            alt="calendar"
            :class="{
              'mobOnly:mb-8': size === 'half'
            }"
          >
          <div>{{ showDate }}</div>
        </div>

        <div v-if="blok.location" class="flex">
          <img
            v-if="blok.icon === 'online'"
            src="/icons/globe.svg"
            class="w-16 desk:w-20 mr-8"
            alt="online"
          >
          <img
            v-if="blok.icon === 'map'"
            src="/icons/location.svg"
            class="w-16 desk:w-20 mr-8"
            alt="location"
          >
          {{ blok.location }}
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { EventEntry } from '~/constants/types/storyblok';
import { useGlobalContentStore } from '~/store/globalContent';
const NuxtLink = resolveComponent('NuxtLink');

const globalContent = useGlobalContentStore();
const props = defineProps<{
  blok: EventEntry,
  size: 'half' | 'full',
  sizeDesktop: 'half' | 'full',
}>();

const startDate = computed(() => {
  return new Date(props.blok.startDate);
});
const endDate = computed(() => {
  if (props.blok.endDate !== '') {
    return new Date(props.blok.endDate);
  }
  return  null;
});

const startMonth = computed(()=> {
  return startDate.value.toLocaleDateString(
    globalContent.getLocale,
    {
      month: 'short',
    }
  );
});

const startDay = computed(()=> {
  return startDate.value.toLocaleDateString(
    globalContent.getLocale,
    {
      day: 'numeric',
    }
  );
});

const showDate = computed(()=> {
  let output = startDate.value.toLocaleDateString(
    globalContent.getLocale,
    {
      month: 'short',
      day: 'numeric',
    }
  );

  if (props.blok.startTime?.trim() !== '') {
    output += ' ' + props.blok.startTime;
  }

  if (endDate.value) {
    output += ' - ';
    output += endDate.value.toLocaleDateString(
      globalContent.getLocale,
      {
        month: 'short',
        day: 'numeric',
      }
    );
  }

  const today = new Date();
  if (today.getFullYear() !== startDate.value.getFullYear()) {
    output += ' ' + startDate.value.getFullYear();
  }

  return output;
});

</script>

<style scoped lang="postcss">
</style>
